
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const TrueFalseSeasons: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'TrueFalseSeasons.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/TrueFalseSeasons.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Seasons – True or False Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore seasons with this engaging UKG true or false worksheet. Simple seasonal statements help young learners build general awareness and reasoning skills." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/seasons/true-false" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Seasons – True or False</h1>

                                    <p>
                                        Help your child explore the seasons of the year with this fun and simple <strong>true or false worksheet</strong> designed for <strong>UKG (Upper Kindergarten)</strong> learners. This activity encourages children to think critically and test their understanding of common seasonal facts.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify different seasons like summer, winter, monsoon, and spring</li>
                                        <li>Understand weather changes across seasons</li>
                                        <li>Distinguish between true and false statements</li>
                                        <li>Improve basic reasoning and observation skills</li>
                                    </ul>

                                    <h2>📝 Worksheet Activity</h2>
                                    <p>
                                        Each statement in this worksheet is based on seasonal facts. Children have to mark them as <strong>True or False</strong>. These include ideas about weather, clothing, and nature.
                                    </p>

                                    <h2>📘 Sample Statements</h2>
                                    <ul>
                                        <li>We wear woolen clothes in summer. – <strong>False</strong></li>
                                        <li>Rain falls during the monsoon season. – <strong>True</strong></li>
                                        <li>Snow falls in spring. – <strong>False</strong></li>
                                        <li>Flowers bloom in spring. – <strong>True</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Benefits of This Activity</h2>
                                    <ul>
                                        <li>Builds seasonal vocabulary and knowledge</li>
                                        <li>Enhances comprehension and logical thinking</li>
                                        <li>Prepares children for classroom assessments</li>
                                        <li>Supports interactive and independent learning</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Seasons – True or False</h1>

                                <p>
                                    Help your child explore the seasons of the year with this fun and simple <strong>true or false worksheet</strong> designed for <strong>UKG (Upper Kindergarten)</strong> learners. This activity encourages children to think critically and test their understanding of common seasonal facts.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify different seasons like summer, winter, monsoon, and spring</li>
                                    <li>Understand weather changes across seasons</li>
                                    <li>Distinguish between true and false statements</li>
                                    <li>Improve basic reasoning and observation skills</li>
                                </ul>

                                <h2>📝 Worksheet Activity</h2>
                                <p>
                                    Each statement in this worksheet is based on seasonal facts. Children have to mark them as <strong>True or False</strong>. These include ideas about weather, clothing, and nature.
                                </p>

                                <h2>📘 Sample Statements</h2>
                                <ul>
                                    <li>We wear woolen clothes in summer. – <strong>False</strong></li>
                                    <li>Rain falls during the monsoon season. – <strong>True</strong></li>
                                    <li>Snow falls in spring. – <strong>False</strong></li>
                                    <li>Flowers bloom in spring. – <strong>True</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Benefits of This Activity</h2>
                                <ul>
                                    <li>Builds seasonal vocabulary and knowledge</li>
                                    <li>Enhances comprehension and logical thinking</li>
                                    <li>Prepares children for classroom assessments</li>
                                    <li>Supports interactive and independent learning</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-4">
                                <h3 className="mb-2">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default TrueFalseSeasons;
